import { useNavigate } from "react-router-dom";
import { influencerProducts, topStars } from "@/mocks/influencerData";

const platforms = [
  { icon: "ri-facebook-fill", color: "text-white", bg: "bg-white/15 border border-white/30", name: "Facebook Stars", users: "3B+ Users" },
  { icon: "ri-youtube-fill", color: "text-white", bg: "bg-white/15 border border-white/30", name: "YouTube Stars", users: "2.7B+ Users" },
  { icon: "ri-twitter-x-fill", color: "text-white", bg: "bg-white/15 border border-white/30", name: "Twitter / X Stars", users: "600M+ Users" },
  { icon: "ri-tiktok-fill", color: "text-white", bg: "bg-white/15 border border-white/30", name: "TikTok Stars", users: "1.5B+ Users" },
];

const InfluencerSection = () => {
  const navigate = useNavigate();
  const featuredProducts = influencerProducts.slice(0, 4);

  return (
    <section className="py-20 relative overflow-hidden" style={{ background: "linear-gradient(135deg, #92400e 0%, #b45309 20%, #d97706 40%, #f59e0b 55%, #fbbf24 65%, #f59e0b 75%, #d97706 88%, #92400e 100%)" }}>
      {/* Gold shimmer overlay */}
      <div className="absolute inset-0 opacity-20" style={{ background: "repeating-linear-gradient(45deg, transparent, transparent 40px, rgba(255,255,255,0.05) 40px, rgba(255,255,255,0.05) 80px)" }}></div>
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-yellow-200 via-white to-yellow-200 opacity-60"></div>
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-yellow-200 via-white to-yellow-200 opacity-60"></div>

      {/* Decorative gold circles */}
      <div className="absolute top-0 right-0 w-96 h-96 rounded-full opacity-10 -translate-y-1/2 translate-x-1/4" style={{ background: "radial-gradient(circle, #fef3c7, transparent)" }}></div>
      <div className="absolute bottom-0 left-0 w-72 h-72 rounded-full opacity-10 translate-y-1/2 -translate-x-1/4" style={{ background: "radial-gradient(circle, #fef3c7, transparent)" }}></div>

      <div className="relative z-10 max-w-screen-xl mx-auto px-4 md:px-8">

        {/* Header */}
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 bg-white/20 border border-white/40 text-yellow-100 text-xs font-bold px-5 py-2 rounded-full mb-6 backdrop-blur-sm">
            <i className="ri-star-fill text-yellow-200"></i>
            Global Star Program · 50,000+ Influencers · 5 Million Verified Factories
          </div>
          <h2 className="text-4xl md:text-6xl font-black text-white mb-5 leading-tight" style={{ fontFamily: "'Playfair Display', serif", textShadow: "0 2px 20px rgba(0,0,0,0.3)" }}>
            Global Stars Help
            <br />
            <span className="text-yellow-200">Middle East Factories</span>
            <br />
            Sell to the World
          </h2>
          <p className="text-yellow-100 text-lg max-w-3xl mx-auto leading-relaxed">
            Facebook · YouTube · Twitter · TikTok stars — <strong className="text-white">choose products from 5 million verified factories</strong> and local warehouses, track your sales in real-time, and earn commissions paid via <strong className="text-white">PayPal</strong>.
          </p>
        </div>

        {/* Platform Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-14">
          {platforms.map((p) => (
            <div key={p.name} className={`${p.bg} backdrop-blur-sm rounded-2xl p-5 text-center hover:-translate-y-1 transition-transform duration-300 cursor-pointer`} onClick={() => navigate("/influencer-program")}>
              <div className="w-14 h-14 flex items-center justify-center bg-white/20 rounded-2xl mx-auto mb-3">
                <i className={`${p.icon} text-3xl ${p.color}`}></i>
              </div>
              <div className="font-black text-white text-sm mb-1">{p.name}</div>
              <div className="text-yellow-200 text-xs">{p.users}</div>
            </div>
          ))}
        </div>

        {/* How it works strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-14">
          {[
            { icon: "ri-box-3-line", step: "1", title: "Pick Products", desc: "Choose from 5M+ verified factory products" },
            { icon: "ri-share-line", step: "2", title: "Share & Promote", desc: "Post on your platform with your unique link" },
            { icon: "ri-bar-chart-line", step: "3", title: "Track Sales", desc: "Real-time dashboard — units sold & earnings" },
            { icon: "ri-paypal-fill", step: "4", title: "Get Paid via PayPal", desc: "Commission paid to your PayPal every month" },
          ].map((item) => (
            <div key={item.step} className="bg-white/15 border border-white/30 backdrop-blur-sm rounded-2xl p-5 text-center">
              <div className="w-12 h-12 flex items-center justify-center bg-white/20 rounded-xl mx-auto mb-3">
                <i className={`${item.icon} text-white text-xl`}></i>
              </div>
              <div className="text-yellow-200 text-xs font-bold mb-1">STEP {item.step}</div>
              <div className="font-black text-white text-sm mb-1">{item.title}</div>
              <div className="text-yellow-100 text-xs leading-relaxed">{item.desc}</div>
            </div>
          ))}
        </div>

        {/* Featured Products to Promote */}
        <div className="mb-14">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-2xl font-black text-white" style={{ fontFamily: "'Playfair Display', serif" }}>
                Hot Products to Promote
              </h3>
              <p className="text-yellow-200 text-sm mt-1">Top-selling verified factory products with highest commissions</p>
            </div>
            <button
              onClick={() => navigate("/influencer-program")}
              className="flex items-center gap-2 bg-white/20 hover:bg-white/30 border border-white/40 text-white font-bold px-5 py-2.5 rounded-xl transition-colors cursor-pointer whitespace-nowrap text-sm"
            >
              View All Products <i className="ri-arrow-right-line"></i>
            </button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {featuredProducts.map((product) => (
              <div key={product.id} className="bg-white rounded-2xl overflow-hidden hover:-translate-y-1 transition-transform duration-300 cursor-pointer" onClick={() => navigate("/influencer-program")}>
                <div className="relative h-44 overflow-hidden">
                  <img src={product.image} alt={product.name} className="w-full h-full object-cover object-top" />
                  <div className="absolute top-2 left-2">
                    <span className="bg-amber-500 text-white text-xs font-black px-2 py-1 rounded-lg">{product.commissionRate}% Commission</span>
                  </div>
                  <div className="absolute top-2 right-2">
                    <span className="bg-white/90 text-gray-700 text-xs font-bold px-2 py-1 rounded-lg">{product.factoryFlag} {product.factoryCountry}</span>
                  </div>
                </div>
                <div className="p-4">
                  <div className="text-xs text-amber-600 font-semibold mb-1 truncate">{product.factory}</div>
                  <div className="font-bold text-gray-900 text-sm mb-3 line-clamp-2 leading-tight">{product.name}</div>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-xs text-gray-400">You earn per sale</div>
                      <div className="font-black text-emerald-600 text-lg">${product.commissionAmount.toFixed(0)}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs text-gray-400">Total sold</div>
                      <div className="font-bold text-gray-700 text-sm">{product.totalSold.toLocaleString()}</div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Top Stars Leaderboard */}
        <div className="mb-14">
          <h3 className="text-2xl font-black text-white mb-6 text-center" style={{ fontFamily: "'Playfair Display', serif" }}>
            Top Earning Stars This Month
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {topStars.map((star, idx) => (
              <div key={star.id} className="bg-white/15 border border-white/30 backdrop-blur-sm rounded-2xl p-5 text-center">
                <div className="relative w-16 h-16 mx-auto mb-3">
                  <img src={star.avatar} alt={star.name} className="w-16 h-16 rounded-full object-cover border-2 border-yellow-300" />
                  <div className="absolute -top-1 -right-1 w-6 h-6 flex items-center justify-center bg-yellow-400 rounded-full text-xs font-black text-amber-900">#{idx + 1}</div>
                </div>
                <div className="font-black text-white text-sm mb-0.5">{star.name}</div>
                <div className="flex items-center justify-center gap-1.5 mb-3">
                  <span className="text-yellow-200 text-xs">{star.flag} {star.country}</span>
                  <span className="text-yellow-300">·</span>
                  <i className={`${star.platformIcon} text-yellow-200 text-xs`}></i>
                  <span className="text-yellow-200 text-xs">{star.followers}</span>
                </div>
                <div className="bg-white/10 rounded-xl p-3">
                  <div className="text-yellow-200 text-xs mb-1">Commission Earned</div>
                  <div className="font-black text-yellow-300 text-xl" style={{ fontFamily: "'Playfair Display', serif" }}>
                    ${star.totalCommission.toLocaleString()}
                  </div>
                  <div className="text-white/60 text-xs mt-0.5">{star.totalSales.toLocaleString()} in sales</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* PayPal Commission Banner */}
        <div className="bg-white/15 border border-white/30 backdrop-blur-sm rounded-3xl px-8 py-8 mb-10 flex flex-col md:flex-row items-center gap-6">
          <div className="w-16 h-16 flex items-center justify-center bg-white rounded-2xl flex-shrink-0">
            <i className="ri-paypal-fill text-3xl" style={{ color: "#003087" }}></i>
          </div>
          <div className="flex-1 text-center md:text-left">
            <div className="font-black text-white text-xl mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>All Commissions Paid via PayPal</div>
            <p className="text-yellow-100 text-sm leading-relaxed">
              Earn up to <strong className="text-yellow-200">30% commission</strong> on every sale. Payments processed monthly directly to your PayPal account — no delays, no hidden fees. Available in <strong className="text-white">190+ countries</strong>.
            </p>
          </div>
          <div className="flex flex-col gap-2 text-center flex-shrink-0">
            <div className="text-yellow-200 text-xs font-bold uppercase tracking-wide">Commission Rates</div>
            <div className="flex gap-2">
              {["18%", "22%", "25%", "30%"].map((r) => (
                <div key={r} className="bg-white/20 border border-white/30 rounded-lg px-3 py-2 text-white font-black text-sm">{r}</div>
              ))}
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center">
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => navigate("/influencer-program")}
              className="bg-white text-amber-700 hover:bg-yellow-50 font-black px-10 py-4 rounded-2xl text-base transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2 justify-center"
            >
              <i className="ri-star-fill text-amber-500"></i>
              Join as a Global Star — Start Earning
            </button>
            <button
              onClick={() => navigate("/influencer-program")}
              className="border-2 border-white/60 text-white hover:bg-white/10 font-bold px-10 py-4 rounded-2xl text-base transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2 justify-center"
            >
              <i className="ri-building-4-line"></i>
              For Factories: Join Program
            </button>
          </div>
          <p className="text-yellow-200 text-sm mt-4">
            Choose products · Track sales · Earn commissions · Get paid via PayPal
          </p>
        </div>
      </div>
    </section>
  );
};

export default InfluencerSection;
